import { isPreproduction, isProduction } from './../../../env'
import { PlpSortStrategy } from '../../../services/api/types'
import { SanityTemplate } from '../types'
import {
  atButton,
  atFormField,
  atLink,
  atNavbarLink,
  iconAsset,
  iconInformativeList,
  imageAsset,
  mediaAsset,
  mlNavbarDropdown,
  nonBookableDates,
  orBaseContent,
  orContainer,
  orContainerTwoCol,
  orFlexibleContent,
  orTabs,
  productId,
  referencedByPgPageCondition,
  smProductCard,
  smWorkspaceCard,
  svgAsset,
} from './helpers'

const footerContent = `{
    logo->${svgAsset},
    copyright,
    contact,
    secondaryLinks[]->${atLink},
    socialMedias[]->${atLink},
    links[]->${atLink}
  }`

const headerContent = `{
    logo->${svgAsset},
    navigationLinks[]->{
      _type == 'mlNavbarDropdown' => ${mlNavbarDropdown},
      _type == 'atLink' => ${atNavbarLink}
    },
    ctaButtons[]->${atButton},
    alternateLink->{href,label}
  }`

const layout = `
  header->${headerContent},
  footer->${footerContent}`

// Conditional query for A/B testing variants - reuses templateSelector for each type
const allTemplatesConditionalQuery = () => {
  const templates: SanityTemplate[] = [
    'tmVisitorGuide',
    'tmContact',
    'tmCheckout',
    'tmGuestList',
    'tmProductListing',
    'tmFlexible',
    'tmBuildingDetail',
  ]

  const conditions = templates.map((type) => `_type == '${type}' => ${templateSelector(type)}`).join(',\n  ')

  return `{
  _type,
  ${conditions}
}`
}

export const pageQuery = (slug: string, templateQuery = '') => `*[_type == "pgPage" && slug == "${slug}"]{
  title,
  ${layout},
  template->${templateQuery},
  "templateId": template->_id,
  abTestingConfiguration->{
    active,
    cookieName,
    pageVariants[]{
      controlVariant,
      variantName,
      trafficAllocation,
      "variantId": content->_id,
      title,
      ${layout},
      seo->,
      content->${allTemplatesConditionalQuery()}
    }
  },
  pageType,
  "id": _id,
}[0]
`

export const pdpQuery = (slug: string) => `*[_type == "pgPage" && product->slug == "${slug.replace(
  /^\//,
  ''
)}" && product->isWorkspaceCard != true${isProduction || isPreproduction ? ' && product->publishSpace == true' : ''}]{
  ${layout},
  product->{
    spaceType,
    productName,
    bookableDays,
    source,
    "platformId": ${productId},
    description,
    "building": buildingRef->slug.current,
    "highlights": highlights->${iconInformativeList},
    spaceCapabilities->${orFlexibleContent},
    additionalServices->${orBaseContent},
    images[]->${imageAsset},
    media[]->${mediaAsset},
    floorplanImage->${imageAsset},
    inquiryOnly,
    inquireSuccessMessage->,
    failureMessage->,
    location->${orTabs(orFlexibleContent)},
    similarSpaces->${orTabs(
      smProductCard,
      'count(*[_type == "pgPage" && product._ref == ^._ref]) > 0 && @->privateSpace != true'
    )},
    otherSolutions[@->isWorkspaceCard][0...2]->${smWorkspaceCard},
    brandMoment->${orBaseContent},
    textBeforePrice,
    inquiryCurrency,
    inquiryPrice,
    rateType,
    textAfterPrice,
    textAfterPricePdp,
    capacity,
    hideCapacityOnPdp,
    squareFootage,
    hideSquareFootageOnPdp,
    earliestBookableHours,
    "minimumBookingDuration": {
      "title": minimumBookingDuration.title,
      "value": minimumBookingDuration.value
    },
  },
  pageType,
  "id": _id,
}[0]`

/**
 * Query for getting products from Sanity, has two different parameters that need to be added for filtering
 * - `buildings`: list of buildings to search from
 * - `types`: array of product types, on Sanity, it should be the same as {ProductType} used in the project
 *   Supports single type (backward compatible) or multiple types (array)
 *   Empty array means no type filter (show all types)
 *
 * The `isWorkspaceCard` is there to filter non products that use the same type for showing workspace cards (See Figma for example)
 */

const plpSmProductAllQueryBase = `_type == "smProduct"${
  isProduction || isPreproduction ? ' && publishSpace == true' : ''
} && privateSpace != true && isWorkspaceCard != true && ${referencedByPgPageCondition}`
const plpSmProductBuildingQueryBase = `_type == "smProduct"${
  isProduction || isPreproduction ? ' && publishSpace == true' : ''
} && privateSpace != true && isWorkspaceCard != true && buildingRef->slug.current in $buildings && ${referencedByPgPageCondition}`

// Helper function to build query with optional type filter
const buildPlpQuery = (baseQuery: string, hasTypeFilter: boolean) => {
  return hasTypeFilter ? `${baseQuery} && spaceType in $types` : baseQuery
}

// For backward compatibility, keep the old query names but they'll be built dynamically
const getPlpSmProductAllQuery = (hasTypeFilter: boolean) => buildPlpQuery(plpSmProductAllQueryBase, hasTypeFilter)
const getPlpSmProductBuildingQuery = (hasTypeFilter: boolean) =>
  buildPlpQuery(plpSmProductBuildingQueryBase, hasTypeFilter)
const capacityQuery = (minimumCapacity: number, maximumCapacity: number) =>
  (isNaN(minimumCapacity) ? '' : ` && capacity >= ${minimumCapacity}`) +
  (isNaN(maximumCapacity) ? '' : ` && capacity <= ${maximumCapacity}`)
const instantBookQuery = ` && inquiryOnly != true && bookableDays > 0 && ${
  isProduction ? 'prodStripePrice' : 'stripePrice'
} != null`

export const plpCountProductQuery = ({
  minimumCapacity,
  maximumCapacity,
  instantBook,
  hasTypeFilter,
}: {
  minimumCapacity: number
  maximumCapacity: number
  instantBook: boolean
  hasTypeFilter: boolean
}) =>
  `count(*[${getPlpSmProductBuildingQuery(hasTypeFilter)}${capacityQuery(minimumCapacity, maximumCapacity)}${
    instantBook ? instantBookQuery : ''
  }])`
export const plpCountProductAllRegionQuery = ({
  minimumCapacity,
  maximumCapacity,
  instantBook,
  hasTypeFilter,
}: {
  minimumCapacity: number
  maximumCapacity: number
  instantBook: boolean
  hasTypeFilter: boolean
}) =>
  `count(*[${getPlpSmProductAllQuery(hasTypeFilter)}${capacityQuery(minimumCapacity, maximumCapacity)}${
    instantBook ? instantBookQuery : ''
  }])`

type PlpQueryBuilderProps = {
  filterByBuildings: boolean
  minimumCapacity: number
  maximumCapacity: number
  instantBook: boolean
  isInitialFetch: boolean
  paginate: boolean
  field: 'price' | 'capacity'
  order: 'asc' | 'desc'
  sortStrategy?: PlpSortStrategy
}

const PLP_RANK_DEFAULT = 9999

export const plpQueryBuilder = ({
  filterByBuildings,
  minimumCapacity,
  maximumCapacity,
  instantBook,
  isInitialFetch,
  paginate,
  order,
  field,
  hasTypeFilter,
  sortStrategy = PlpSortStrategy.Standard,
}: PlpQueryBuilderProps & { hasTypeFilter: boolean }) => {
  const sanityField = field === 'price' ? (isProduction ? 'prodStripePrice' : 'stripePrice') : field
  const useManualSort = sortStrategy === PlpSortStrategy.Manual
  const fieldOrderExpr = order === 'desc' ? `coalesce(${sanityField}, -1) ${order}` : `${sanityField} ${order}`
  const orderQuery = useManualSort
    ? `order(coalesce(plpRank, ${PLP_RANK_DEFAULT}) asc, ${fieldOrderExpr})`
    : `order(${fieldOrderExpr})`
  const priceOrderQuery = field === 'price' ? '| order(coalesce(inquiryOnly, false) asc)' : ''
  const comparationOrder = order === 'asc' ? '>' : '<'
  const includeNulls = `($lastField != null && ${sanityField} == null)`

  let query = ''
  query = query.concat('*[')
  query = query.concat(
    filterByBuildings ? getPlpSmProductBuildingQuery(hasTypeFilter) : getPlpSmProductAllQuery(hasTypeFilter)
  )
  query = query.concat(capacityQuery(minimumCapacity, maximumCapacity))
  query = query.concat(instantBook ? instantBookQuery : '')

  if (!isInitialFetch) {
    // Build the field + _id cursor condition (used by all sort strategies)
    const fieldCursor = `(${sanityField} ${comparationOrder} $lastField || ${includeNulls})
        || (${sanityField} == $lastField && _id > $lastId)`

    // For price sort, the final order is: order(price) | order(inquiryOnly asc).
    // The cursor must mirror both levels: inquiryOnly as the primary dimension
    // (false sorts before true), then price + _id as tiebreakers.
    const sortFieldCursor =
      field === 'price'
        ? `(coalesce(inquiryOnly, false) > coalesce($lastInquiryOnly, false))
        || (coalesce(inquiryOnly, false) == coalesce($lastInquiryOnly, false) && (
          ${fieldCursor}
        ))`
        : fieldCursor

    if (useManualSort) {
      const lastPlpRank = `coalesce($lastPlpRank, ${PLP_RANK_DEFAULT})`
      const currentPlpRank = `coalesce(plpRank, ${PLP_RANK_DEFAULT})`
      query = query.concat(` && (
      (${currentPlpRank} > ${lastPlpRank})
      || (${currentPlpRank} == ${lastPlpRank} && (
        ${sortFieldCursor}
      ))
      )`)
    } else {
      query = query.concat(` && (
      ${sortFieldCursor}
      )`)
    }
  }
  query = query.concat(`] | ${orderQuery} ${priceOrderQuery} `)
  if (paginate) {
    query = query.concat('[0...8] ')
  }
  query = query.concat(smProductCard)
  return query
}

export const buildingHolidaysQuery = `*[_type == "smBuilding" && slug.current == $building][0]{
  "nonBookableDates": nonBookableDates->${nonBookableDates}
}`

export const buildingDetailsQuery = `*[_type == "smBuilding" && slug.current == $building][0]{
  "nonBookableDates": nonBookableDates->${nonBookableDates},
  isNonStudioLocation,
  nonStudioLocationExternalLink->${atLink}
}`

/** Query for a template when `product` is not defined in the page, otherwise query for the slug inside the product (lacking the initial `\`) */
export const initialPageQuery = (slug: string) => `
*[_type == "pgPage" && ((product == null && slug == "${slug}") || (product->slug == "${slug.replace(
  /^\//,
  ''
)}" && product->isWorkspaceCard != true${
  isProduction || isPreproduction ? ' && product->publishSpace == true' : ''
}))]{ 
  "template": template->_type, 
  product->{source,"platformId": ${productId},productName}, 
  title, 
  seo->,
  "templateId": template->_id,
  abTestingConfiguration->{
    active,
    cookieName,
    pageVariants[]{
      controlVariant,
      variantName,
      trafficAllocation,
      "variantId": content->_id,
      title,
      seo->
    }
  }
}[0]`

export const redirectSlugsQuery = () => `
  *[_type == "smRedirectSlugs"][0] { redirectSlugs[] {from, to }}.redirectSlugs
`

export const plpSortABTestingQuery = () => `
*[_type == "smPlpSortABTestingConfig" && active == true][0]{
  active,
  cookieName,
  variants[]{
    variantName,
    sortStrategy,
    trafficAllocation
  }
}
`

export const moduleABTestingQuery = () => `
*[_type == "smModuleABTestingConfiguration" && active == true][0]{
  active,
  moduleVariants[]{
    type,
    cookieName,
    variants[]{
      controlVariant,
      variantName,
      trafficAllocation,
      "variantId": content->_id,
      content->{
        _type,
        _type == 'orNavbar' => ${headerContent},
        _type == 'orFooter' => ${footerContent}
      }
    }
  }
}
`

export const templateSelector = (type: SanityTemplate) => {
  switch (type) {
    case 'tmVisitorGuide':
      return `{
        heading,
        pageLinks[]->${atLink},
        location{
          heading,
          addressInfo,
          mapTile->{
            "id": _id,
            address,
            details->{
              items[]{icon->${iconAsset},title}
            }
          }
        },
        content[]->{
          "id": _id,
          layout,
          sectionTitle,
          images[]->${imageAsset},
          media[]->${mediaAsset},
          sectionBody,
          sectionDescription,
          list->{
            items[]{icon->${iconAsset},title,body}
          }
        }
      }`
    case 'tmContact':
      return `{
        description,
        contactImage->{...image},
        contactInfo,
        snackbarStatus->,
        serverErrorNotification->,
        singleFieldErrorNotification->,
        multipleFieldsErrorNotification->,
        contactForm->{
          "id": _id,
          name,
          submitButton->,
          successSnackbar->,
          serverErrorNotification->,
          singleFieldErrorNotification->,
          multipleFieldsErrorNotification->,
          fields[]->{
            _type == 'atFormField' => ${atFormField},
            _type == 'orTabs' => ${orTabs(atFormField)}
          }
        }
      }`
    case 'tmContactV2':
      return `{
        title,
        description,
        submitButtonLabel,
        inquiryTabs[]{
          tabKey,
          tabLabel,
          tabDescription,
          tabIcon->{"src": icon.asset->url, "alt": icon.alt},
          form->{
            "id": _id,
            name,
            successSnackbar->,
            serverErrorNotification->,
            singleFieldErrorNotification->,
            multipleFieldsErrorNotification->,
            fields[]->{
              _type == 'atFormField' => ${atFormField},
              _type == 'orTabs' => ${orTabs(atFormField)}
            }
          }
        },
        "formSuccess": formSuccess->{ heading, subheading, ctaLabel, ctaHref },
        successNotification->,
        serverErrorNotification->,
        singleFieldErrorNotification->,
        multipleFieldsErrorNotification->
      }`
    case 'tmCheckout':
      return `{
        title,
        steps[]->{
          "id": _id,
          name,
          title,
          fields[]->{
            inputType == 'grid' => {inputType,label,grid[]->{...,icon->${iconAsset}}},
            inputType != 'grid' => {...,icon->${iconAsset}}
          },
          submitButton->{label,variant}
        },
        confirmation{
          ...,
          icon->${iconAsset},
          redirectLink->${atLink}
        },
        serverErrorNotification->,
        singleFieldErrorNotification->,
        multipleFieldsErrorNotification->,
        bookingUnavailableError->,
        declinedCreditCardError->,
        redirectButton->${atLink}
      }`
    case 'tmGuestList':
      return `{
        title,
        label,
        content,
        notification->,
        cloudgateErrorNotification->,
        fields[]->{...,icon->${iconAsset}},
        serverErrorNotification->,
        successInfo{title,icon->${iconAsset},button->${atButton}},
        registrationExpiredNotification->
      }`
    case 'tmProductListing':
      return `{
        notFindingCard{
          contactButton->${atButton},
          notFindingImage->${imageAsset},
          notFindingTitle
        },
        loadButtonLabel,
        notFoundText,
        notFoundTitle,
        "contactDetails": contactDetails->${iconInformativeList}
      }`
    case 'tmFlexible':
      return `{
        hideTitle,
        "id": _id,
        blocks[]->{
          _type == 'orContainer' => ${orContainer},
          _type == 'orContainerTwoCol' => ${orContainerTwoCol},
          _type == 'orBaseContent' => ${orBaseContent}
        }
      }`
    case 'tmBuildingDetail':
      return `{
          buildingImages[]->${mediaAsset},
          pageLinks[]->${atLink},
          content[]->{
            layout,
            logo->{...image},
            sectionTitle,
            sectionTitle2,
            sectionSubtitle,
            sectionDescription,
            sectionDescription2,
            sectionBody,
            sectionFooter,
            outline,
            outline2,
            handwrittenDetail,
            images[]->${imageAsset},
            button->{
              label,
              variant,
              target,
              actionType,
              actionValue
            },
            cards[]->{
              images->{
                images[]->{...image}
              },
              outline,
              title,
              description,
              additionalInfo,
            },
            fieldName,
            tabsTitle,
            name,
            tabs[]->{
              label,
              content[]->{
                contentTitle,
                detailsBody,
                images[]->${imageAsset},
                details->{
                  items[]{icon->${iconAsset},title,body,link->}
                }
              }
            }
          },
          address{
            heading,
            addressInfo,
            directionLink->${atLink},
            mapTile->{
              address,
              details->{
                items[]{icon->${iconAsset},title}
              }
            }
          },
          spacesSection{
            allSpacesButton->{
              label,
              actionValue,
              actionType,
            },
            spaceType
          },
          "building": buildingRef->slug.current,
          "isNonStudioLocation": buildingRef->isNonStudioLocation
        }`
    default:
      return ''
  }
}

export const buildingsQuery = `*[_type == "smBuilding"]{
  "slug": slug.current,
  name,
  displayName,
  active,
  cloudgateEnabled,
  contactEmail,
  eventsContactEmail,
  prodYardikubeLocationId,
  sandboxYardikubeLocationId,
  prodTripleseatLocationId,
  sandboxTripleseatLocationId,
  "city": city->{ name, "slug": slug.current, timezone, currency, taxPercentage }
}`
